import { dateFnsLocale } from 'common/utils/date'
import { CHART_SUPPORT_THRESHOLD } from 'common/constants/chart'
import { format } from 'date-fns'
import { enUS, th } from 'date-fns/locale'
import i18n from 'i18n'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'

/** Format CHART_SUPPORT_THRESHOLD (ISO string) as a short Thai date, e.g. "10 เม.ย. 2569" */
function formatThresholdDate(iso: string): string {
  try {
    if ((i18n.language ?? '').startsWith('en')) return format(new Date(iso), 'd MMM yyyy', { locale: enUS })
    // Buddhist Era offset: +543 years
    const date = new Date(iso)
    const day = format(date, 'd', { locale: dateFnsLocale() })
    const month = format(date, 'MMM', { locale: dateFnsLocale() })
    const year = date.getFullYear() + 543
    return `${day} ${month} ${year}`
  } catch {
    return iso
  }
}

interface ChartFallbackProps {
  /** Height of the fallback container (default: 320) */
  height?: number
  /** Main title text */
  title?: string
  /** Secondary description text */
  description?: string
  /** Path to the illustration image */
  img?: string
}

/**
 * Fallback UI displayed when chart data is unavailable
 * (e.g. data only exists from a certain date onward).
 * The threshold date is derived from CHART_SUPPORT_THRESHOLD automatically.
 */
export default function ChartFallback({
  height = 320,
  title,
  description,
  img = '/static/chart/chart_error.svg',
}: ChartFallbackProps) {
  const { t } = useTranslation('common')
  const titleText = title ?? t('chart.fallback.title')
  const descriptionText =
    description ??
    t('chart.fallback.description', { date: formatThresholdDate(CHART_SUPPORT_THRESHOLD) })
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: (theme) => theme.spacing(1.5),
        py: (theme) => theme.spacing(4),
      }}
    >
      <Box
        component="img"
        src={img}
        alt="chart unavailable"
        sx={{ width: 184, height: 117, objectFit: 'contain' }}
      />

      <Typography
        variant="body1"
        fontWeight={600}
        textAlign="center"
        sx={{ color: 'text.primary' }}
      >
        {titleText}
      </Typography>

      {descriptionText && (
        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}
        >
          {descriptionText}
        </Typography>
      )}
    </Box>
  )
}
